import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

interface AdaIconProps {
  size?: number
  className?: string
  showHoverEffects?: boolean
  withShadow?: boolean
  isThinking?: boolean
  isResponding?: boolean
  animationDuration?: number
}

const useStyles = makeStyles(() => ({
  adaRing: {
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  withHover: {
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
      },
    },
  },
  wrapper: {
    borderRadius: '50%',
    display: 'inline-block',
    boxShadow: '0 4px 12px rgba(23, 81, 208, 0.2)',
    transition: 'box-shadow 0.2s ease',
    '@media (hover: hover)': {
      '&:hover': {
        boxShadow: '0 6px 16px rgba(23, 81, 208, 0.3)',
      },
    },
  },
  wrapperAnimating: {
    borderRadius: '50%',
    display: 'inline-block',
    boxShadow: '0 4px 12px rgba(23, 81, 208, 0.2)',
  },
  thinking: {
    animation: '$adaIconPulse var(--ada-animation-duration, 1s) ease-out infinite',
    animationDelay: '0ms',
  },
  responding: {
    animation: '$spin var(--ada-animation-duration, 0.8s) linear infinite',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  '@keyframes adaIconPulse': {
    '0%, 100%': {
      boxShadow: '0 4px 4px rgba(23, 81, 208, 0.2)',
      transform: 'scale(1)',
    },
    '50%': {
      boxShadow: '0 8px 8x rgba(23, 81, 208, 0.3)',
      transform: 'scale(1.05)',
    },
  },
}))

const AdaIcon: React.FC<AdaIconProps> = ({
  size = 24,
  className = '',
  showHoverEffects = false,
  withShadow = false,
  isThinking = false,
  isResponding = false,
  animationDuration = 1000,
}) => {
  const classes = useStyles()

  const borderWidth = Math.round(size * 0.25)

  const ringStyle = {
    width: size,
    height: size,
    border: `${borderWidth}px solid transparent`,
    background: 'linear-gradient(180deg, #1751D0 0%, #53A7FC 100%) border-box',
    WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
  }

  const hasAnimations = isThinking || isResponding

  const ringClassNames = [classes.adaRing, showHoverEffects && !hasAnimations && classes.withHover, className]
    .filter(Boolean)
    .join(' ')

  const iconElement = <div className={ringClassNames} style={ringStyle} />

  if (withShadow || hasAnimations) {
    const wrapperClasses = [
      hasAnimations ? classes.wrapperAnimating : classes.wrapper,
      isResponding && classes.responding,
      isThinking && !isResponding && classes.thinking,
    ]
      .filter(Boolean)
      .join(' ')

    const animationStyle: React.CSSProperties = {
      '--ada-animation-duration': `${animationDuration}ms`,
    } as React.CSSProperties

    return (
      <div className={wrapperClasses} style={animationStyle}>
        {iconElement}
      </div>
    )
  }

  return iconElement
}

export default AdaIcon
